import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { updateTrailerVehicleScheme } from "../../../validations/createVehicleScheme";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Divider,
  IconButton,
  ImageListItem,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import { getDate, getVehicleType } from "../../../utils";
import { ReactComponent as UploadIcon } from "../../../assets/svg/uploaddoc.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/svg/deleteFile.svg";
import styled from "styled-components";
import VehiclePlaceholderImage from "../../../assets/images/vehicle_placeholder.jpeg";
import { useTranslation } from "react-i18next";

const UpdateTrailerForm = ({
  submitForm,
  selectedVehicle,
  disableSubmit = false,
}) => {
  const { t } = useTranslation();

  const [uploadLicensePlate, setUploadLicensePlate] = useState(null);
  const [uploadLicensePlateImage, setUploadLicensePlateImage] = useState(null);
  const [uploadInsuranceFile, setUploadInsuranceFile] = useState(null);
  const [uploadInsuranceImage, setUploadInsuranceImage] = useState(null);
  const [isInsuranceRemoved, setIsInsuranceRemoved] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleLicensePlateImageUploadChange = (e) => {
    const uploadedFile = URL.createObjectURL(e.target.files[0]);
    setUploadLicensePlate(e.target.files[0]);
    setUploadLicensePlateImage(uploadedFile);
  };
  const handleInsuranceUploadChange = (e) => {
    const uploadedFile = URL.createObjectURL(e.target.files[0]);
    setUploadInsuranceFile(e.target.files[0]);
    setUploadInsuranceImage(uploadedFile);
  };
  const [isLicensePlateRemoved, setIsLicensePlateRemoved] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: "all",
    defaultValues: selectedVehicle,
    resolver: yupResolver(updateTrailerVehicleScheme),
  });
  const fm = selectedVehicle?.fileUploadResponse
    ? selectedVehicle?.fileUploadResponse.find(
        (fmt) => fmt.mediaCode === "LICPL",
      )
    : "";

  const insuranceFile = selectedVehicle?.fileUploadResponse
    ? selectedVehicle?.fileUploadResponse.find(
        (fmt) => fmt.mediaCode === "PROOFINS",
      )
    : "";

  return (
    <form
      onSubmit={handleSubmit((data) => {
        if (!uploadInsuranceFile) {
          setErrorMessage(t("components.updateTrailerVehicleForm.text02"));
          return;
        }
        submitForm(
          data,
          false,
          isLicensePlateRemoved,
          isInsuranceRemoved,
          null,
          uploadLicensePlate,
          uploadInsuranceFile,
        );
        reset({});
      })}
    >
      <Stack spacing={2}>
        <Controller
          name="vehicleType"
          control={control}
          disabled={true}
          render={({ field }) => (
            <TextField
              {...field}
              name="vehicleType"
              type="text"
              id="standard-required"
              label={t("pages.addVehicle.text01")}
              defaultValue={getVehicleType(selectedVehicle?.veh_type_cd)}
              variant="standard"
              className="edit_field"
              size="small"
              margin="none"
              sx={{
                "& .MuiStack-root": {
                  paddingTop: "20% !important",
                },
              }}
              error={!!errors.vehicleType}
              helperText={errors.vehicleType?.message}
            />
          )}
        ></Controller>
        <Controller
          name="trailerNumber"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              name="trailerNumber"
              type="trailerNumber"
              label="Trailer Number"
              variant="standard"
              className="edit_field"
              size="small"
              margin="none"
              sx={{
                "& .MuiStack-root": {
                  paddingTop: "20% !important",
                },
              }}
              error={!!errors.trailerNumber}
              helperText={errors?.trailerNumber?.message}
              defaultValue={selectedVehicle?.trailerNumber}
            />
          )}
        ></Controller>
        <Controller
          name="plateNumber"
          control={control}
          disabled={true}
          render={({ field }) => (
            <TextField
              {...field}
              name="plateNumber"
              type="text"
              id="standard-required"
              label={t("pages.addVehicle.text06")}
              defaultValue={selectedVehicle?.lic_plate_num}
              variant="standard"
              className="edit_field"
              size="small"
              margin="none"
              sx={{
                "& .MuiStack-root": {
                  paddingTop: "20% !important",
                },
              }}
              error={!!errors.plateNumber}
              helperText={errors.plateNumber?.message}
            />
          )}
        ></Controller>
        {errorMessage && <Typography color="error">{errorMessage}</Typography>}
        <div style={{ alignItems: "center", flexDirection: "row" }}>
          <div
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ListItem alignItems="center" sx={{ paddingLeft: 0 }}>
              <ImageListItem>
                <img
                  alt=""
                  style={{
                    width: 60,
                    height: 60,
                    top: 12,
                    borderRadius: 8,
                  }}
                  loading="lazy"
                  src={
                    uploadLicensePlateImage
                      ? uploadLicensePlateImage
                      : fm?.tempUrlTumbs
                        ? isLicensePlateRemoved
                          ? VehiclePlaceholderImage
                          : fm?.tempUrlTumbs
                        : VehiclePlaceholderImage
                  }
                />
              </ImageListItem>
              <ListItemText
                primary={
                  <div
                    style={{
                      flexDirection: "row",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      color="text.primary"
                      style={{
                        fontFamily: "Segoe UI",
                        fontSize: 16,
                        fontWeight: 600,
                        textAlign: "left",
                        letterSpacing: "0.5%",
                      }}
                    >
                      {t("components.updatePersonalVehicleForm.text01")}
                    </Typography>
                  </div>
                }
                style={{
                  fontFamily: "Segoe UI",
                  fontSize: 16,
                  fontWeight: 600,
                  textAlign: "left",
                  letterSpacing: "0.5%",
                }}
                sx={{ marginLeft: 2 }}
                secondary={
                  <React.Fragment>
                    {fm?.mediaUpdDate && (
                      <Typography
                        sx={{ display: "inline" }}
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {`Uploaded on ${getDate(fm?.mediaUpdDate)}`}
                      </Typography>
                    )}
                  </React.Fragment>
                }
              />
            </ListItem>

            <div style={{ flexDirection: "row", display: "flex" }}>
              <Tooltip title="Upload" arrow>
                <IconButton
                  aria-label="upload"
                  component="label"
                  sx={{ padding: 0 }}
                >
                  <>
                    <UploadIcon style={{ width: 40, height: 40 }} />
                    <VisuallyHiddenInput
                      type="file"
                      onChange={handleLicensePlateImageUploadChange}
                      accept="image/png, image/jpeg, application/pdf"
                    />
                  </>
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove" arrow>
                <IconButton
                  aria-label="upload"
                  component="label"
                  onClick={() => {
                    setUploadLicensePlate(null);
                    setUploadLicensePlateImage(null);
                    setIsLicensePlateRemoved(true);
                  }}
                  sx={{ padding: 0 }}
                >
                  <DeleteIcon style={{ width: 40, height: 40 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
        <div style={{ alignItems: "center", flexDirection: "row" }}>
          <div
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ListItem alignItems="center" sx={{ paddingLeft: 0 }}>
              <ImageListItem>
                <img
                  alt=""
                  style={{
                    width: 60,
                    height: 60,
                    top: 12,
                    borderRadius: 8,
                  }}
                  loading="lazy"
                  src={
                    uploadInsuranceImage
                      ? uploadInsuranceImage
                      : insuranceFile?.tempUrlTumbs
                        ? isInsuranceRemoved
                          ? VehiclePlaceholderImage
                          : insuranceFile?.tempUrlTumbs
                        : VehiclePlaceholderImage
                  }
                />
              </ImageListItem>

              <ListItemText
                primary={
                  <div
                    style={{
                      flexDirection: "row",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      color="text.primary"
                      style={{
                        fontFamily: "Segoe UI",
                        fontSize: 16,
                        fontWeight: 600,
                        textAlign: "left",
                        letterSpacing: "0.5%",
                      }}
                    >
                      {t("components.updateTrailerVehicleForm.text01")}
                    </Typography>
                  </div>
                }
                style={{
                  fontFamily: "Segoe UI",
                  fontSize: 16,
                  fontWeight: 600,
                  textAlign: "left",
                  letterSpacing: "0.5%",
                }}
                sx={{ marginLeft: 2 }}
                secondary={
                  <React.Fragment>
                    {insuranceFile?.mediaUpdDate && (
                      <Typography
                        sx={{ display: "inline" }}
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {`Uploaded on ${getDate(insuranceFile?.mediaUpdDate)}`}
                      </Typography>
                    )}
                  </React.Fragment>
                }
              />
            </ListItem>

            <div style={{ display: "flex" }}>
              <Tooltip title="Upload" arrow>
                <IconButton
                  component="label"
                  aria-label="upload"
                  sx={{ padding: 0 }}
                >
                  <>
                    <UploadIcon style={{ width: 40, height: 40 }} />
                    <VisuallyHiddenInput
                      type="file"
                      onChange={handleInsuranceUploadChange}
                      accept="image/png, image/jpeg, application/pdf"
                    />
                  </>
                </IconButton>
              </Tooltip>

              <Tooltip title="Remove" arrow>
                <IconButton
                  onClick={() => {
                    setUploadInsuranceFile(null);
                    setUploadInsuranceImage(null);
                    setIsInsuranceRemoved(true);
                  }}
                  sx={{ padding: 0 }}
                >
                  <DeleteIcon style={{ width: 40, height: 40 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <Divider
            variant="fullWidth"
            color="lightGray"
            style={{ width: "100%" }}
          />
        </div>

        <Button
          type="submit"
          variant="contained"
          alignSelf={"center"}
          disabled={disableSubmit || !isValid}
          style={{
            alignSelf: "center",
            width: 199,
            height: 32,
            marginLeft: 88,
            alignItems: "center",
            margin: "auto",
            marginTop: 50,
            display: "flex",
          }}
          className="update_button"
          onClick={() => null}
        >
          <Typography variant="buttonText" color="common.buttonText">
            {t("commonLabels.text16")}
          </Typography>
        </Button>
      </Stack>
    </form>
  );
};

export default UpdateTrailerForm;

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});
